<template>
  <div class="flex flex-col sm:h-screen">
    <div class="flex-1 flex flex-col sm:flex-row overflow-hidden">
      <OrgComponentsPanel
        ref="orgPanel"
        :org-id="org.id"
        :drag-active="dragPayload?.source === 'catalog'"
        @drop="handleDropOnOrg"
        @drag-start="onOrgDragStart"
        @drag-end="onDragEnd"
      />
      <GlobalCatalogPanel
        v-model:search="catalogSearch"
        :components="availableGlobalComponents"
        :loading="globalStore.loading"
        :drag-active="dragPayload?.source === 'org'"
        @add="addFromCatalog"
        @drop="handleDropOnCatalog"
        @drag-start="onCatalogDragStart"
        @drag-end="onDragEnd"
        @add-custom="(prefillName) => orgPanel?.openAddCustom(prefillName)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useOrgComponentsStore } from 'stores/orgComponents.js'
import { useOrgComponentOptionsStore } from 'stores/orgComponentOptions.js'
import { useGlobalComponentsStore } from 'stores/globalComponents.js'
import OrgComponentsPanel from 'controllers/vue_components/organizations/it_components/OrgComponentsPanel.vue'
import GlobalCatalogPanel from 'controllers/vue_components/organizations/it_components/GlobalCatalogPanel.vue'
import { typeLabel } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'

const orgGlobalStore = orgStore()
const orgComponentsStore = useOrgComponentsStore()
const orgComponentOptionsStore = useOrgComponentOptionsStore()
const globalStore = useGlobalComponentsStore()

const org = computed(() => orgGlobalStore.current_org || {})

watch(() => org.value.name, (name) => {
  if (name) document.title = `IT Components | ${name}`
}, { immediate: true })

const orgPanel = ref(null)
const catalogSearch = ref('')

// Which panel a drag currently originated from, so each panel knows whether
// to render itself as a valid drop target (and which action a drop resolves
// to) — 'catalog' means "adding to the org", 'org' means "removing from it".
const dragPayload = ref(null)

const availableGlobalComponents = computed(() => {
  // Uses the unfiltered, unpaginated options store for exclusion rather than
  // orgComponentsStore.components (the left panel's own search/type-filtered,
  // paginated list) — otherwise narrowing the left search would shrink this
  // set and make already-owned components reappear as "available" here.
  const ownNames = new Set(orgComponentOptionsStore.components.map((c) => c.name.toLowerCase()))
  const query = catalogSearch.value.trim().toLowerCase()

  return globalStore.components.filter((gc) => {
    if (ownNames.has(gc.name.toLowerCase())) return false
    if (!query) return true
    // Matches name, vendor, or category — typeLabel(gc.type) so a search for
    // "database" (the human label) matches even though the raw enum value
    // stored on the record is "database"/"caching"/etc, not always the same
    // word a user would type (e.g. "cache" wouldn't match the raw "caching").
    return gc.name.toLowerCase().includes(query)
      || (gc.vendor || '').toLowerCase().includes(query)
      || typeLabel(gc.type).toLowerCase().includes(query)
  })
})

onMounted(() => {
  globalStore.fetchIfNeeded(org.value.id)
})

function onCatalogDragStart(component) {
  dragPayload.value = { source: 'catalog', component }
}

function onOrgDragStart(component) {
  dragPayload.value = { source: 'org', component }
}

function onDragEnd() {
  dragPayload.value = null
}

function handleDropOnOrg() {
  if (dragPayload.value?.source === 'catalog') addFromCatalog(dragPayload.value.component)
  dragPayload.value = null
}

function handleDropOnCatalog() {
  if (dragPayload.value?.source === 'org') removeFromOrg(dragPayload.value.component)
  dragPayload.value = null
}

// Dragging (or clicking "+" on) a catalog row no longer adds it straight to
// the org's stack — it opens OrgComponentsPanel's slide-over prefilled from
// the catalog entry so the user can review/adjust type & vendor first.
// Nothing reaches the backend, and the org's stack doesn't change, until the
// user actually clicks Save inside that panel (or nothing happens at all if
// they click Cancel).
function addFromCatalog(component) {
  orgPanel.value?.openAddFromComponent(component)
}

async function removeFromOrg(component) {
  const res = await orgComponentsStore.removeComponentOptimistic(component.id)

  if (!res.js_error && res.response.success) {
    orgPanel.value?.refreshCategoryOptions()
  } else {
    orgPanel.value?.notify(`Couldn't remove ${component.name}. Please try again.`, 'alert')
  }
}
</script>
